import type { IssuedBondPricePoint } from '../../../views/AdminDashboard/BondsPlayground/IssuedBondPriceChart';

export const SYNTHETIC_BOND_ISSUANCE_TENORS = [2, 3, 5, 7, 10] as const;
export const SYNTHETIC_BOND_CURVE_TENOR_MONTHS = [
  6, 12, 24, 36, 60, 84, 120,
] as const;

export type SyntheticBondCurveTenorMonths =
  (typeof SYNTHETIC_BOND_CURVE_TENOR_MONTHS)[number];
export type SyntheticBondCurveTenorKey = `${SyntheticBondCurveTenorMonths}`;

export type SyntheticBondSourceMap = Record<
  SyntheticBondCurveTenorKey,
  number | null
>;

export const syntheticBondTenorLabel = (tenorMonths: number) =>
  tenorMonths < 12 ? `${tenorMonths}M` : `${tenorMonths / 12}Y`;

export const syntheticBondSourceNodeMonths = (node: {
  tenor_months?: number;
  tenor_years?: number;
}) => Number(node.tenor_months ?? Number(node.tenor_years) * 12);

export const SYNTHETIC_BOND_FAMILY_METADATA_FIELDS = [
  'geography',
  'region',
  'market_hours_id',
  'country_code',
  'domicile',
  'asset_sector',
] as const;

export type SyntheticBondFamilyMetadata = {
  geography: string;
  region: number | null;
  market_hours_id: number | null;
  country_code: string;
  domicile: string;
  issuer_type: string;
  asset_sector: string;
};

export type SyntheticBondGenerationDraft = {
  series_id: string | null;
  currency_id: number | null;
  family_label: string;
  source_asset_ids_by_tenor_months: SyntheticBondSourceMap;
  family_metadata: SyntheticBondFamilyMetadata;
  initial_tenors: number[];
  first_issue_date: string | null;
  last_issue_date: string | null;
  target_kind: string;
};

export const emptySyntheticBondSourceMap = (): SyntheticBondSourceMap => ({
  '6': null,
  '12': null,
  '24': null,
  '36': null,
  '60': null,
  '84': null,
  '120': null,
});

export const emptySyntheticBondFamilyMetadata =
  (): SyntheticBondFamilyMetadata => ({
    geography: '',
    region: null,
    market_hours_id: null,
    country_code: '',
    domicile: '',
    issuer_type: '',
    asset_sector: '',
  });

export const initialSyntheticBondGenerationDraft =
  (): SyntheticBondGenerationDraft => ({
    series_id: null,
    currency_id: null,
    family_label: '',
    source_asset_ids_by_tenor_months: emptySyntheticBondSourceMap(),
    family_metadata: emptySyntheticBondFamilyMetadata(),
    // Generation is Series-first and a Series has exactly one initial tenor.
    // Default to the first supported tenor so a new Series is immediately a
    // valid draft; the admin can choose another tenor before saving it.
    initial_tenors: [SYNTHETIC_BOND_ISSUANCE_TENORS[0]],
    first_issue_date: null,
    last_issue_date: null,
    target_kind: 'AFS_BOND',
  });

export type SyntheticBondGenerationTarget = {
  key: string;
  label: string;
  enabled: boolean;
  reason?: string;
};

export type SyntheticBondGenerationCurrency = {
  id: number;
  symbol: string;
  name?: string;
};

export type SyntheticBondChoice = {
  id?: number | string;
  value?: number | string;
  label?: string;
  name?: string;
  geography?: string;
};

export type SyntheticBondGenerationOptions = {
  targets: SyntheticBondGenerationTarget[];
  issuance_tenors: number[];
  curve_tenors: Array<{ tenor_months: number; label: string }>;
  issue_schedule: { day: number; months: number[] };
  currencies: SyntheticBondGenerationCurrency[];
  family_metadata_schema: {
    required_fields: string[];
    region_choices?: SyntheticBondChoice[];
    market_hours?: SyntheticBondChoice[];
    field_max_lengths?: Record<string, number>;
  };
  publication_version?: string;
  family_definition_schema_version?: string;
  pricing_version?: string;
  identifier_scheme_version?: string;
  identifier_key_id?: string;
};

export type SyntheticBondSourceAsset = {
  id: number;
  model: string;
  name: string;
  ticker?: string | null;
  ric?: string | null;
};

export type SyntheticBondSourceAssetOption = Pick<
  SyntheticBondSourceAsset,
  'id' | 'name' | 'ticker' | 'ric'
>;

export type SyntheticBondSourceAssetSearchResponse = {
  results: SyntheticBondSourceAssetOption[];
};

export type SyntheticBondReferenceCurvePresetNode = {
  tenor_months: SyntheticBondCurveTenorMonths;
  tenor_label: string;
  asset: SyntheticBondSourceAssetOption;
};

export type SyntheticBondReferenceCurvePreset = {
  id: string;
  code: string;
  label: string;
  is_active: boolean;
  currency: SyntheticBondGenerationCurrency;
  source_asset_ids_by_tenor_months: SyntheticBondSourceMap;
  source_nodes: SyntheticBondReferenceCurvePresetNode[];
  created_at?: string;
  updated_at?: string;
};

export type SyntheticBondReferenceCurvePresetResponse = {
  results: SyntheticBondReferenceCurvePreset[];
};

export type SyntheticBondSeries = {
  id: string;
  code: string;
  country_code: string;
  initial_tenor_years: number;
  currency: SyntheticBondGenerationCurrency;
  reference_curve_preset_id: string;
  reference_curve_preset: {
    id: string;
    code: string;
    label: string;
    is_active: boolean;
  };
  source_asset_ids_by_tenor_months: SyntheticBondSourceMap;
  family_label: string;
  family_metadata_defaults: Omit<
    SyntheticBondFamilyMetadata,
    'country_code' | 'issuer_type'
  >;
  is_active: boolean;
  member_count: number;
  earliest_issue_date: string | null;
  latest_issue_date: string | null;
  configuration_hash: string;
  created_at?: string;
  updated_at?: string;
};

export type SyntheticBondSeriesResponse = {
  results: SyntheticBondSeries[];
};

export type SyntheticBondCatalogueEntry = {
  row_id: string;
  series_id: string;
  series_code: string;
  series_active: boolean;
  series_member_count: number;
  country_code: string;
  initial_tenor_years: number;
  currency: SyntheticBondGenerationCurrency;
  family_label: string;
  reference_curve: {
    id: string;
    code: string;
    label: string;
  };
  member_id: string | null;
  generated_artifact_id: string | null;
  underlying_asset_id: number | null;
  name: string | null;
  ticker: string | null;
  ric: string | null;
  isin: string | null;
  legal_issue_date: string | null;
  legal_maturity_date: string | null;
  priced_from: string | null;
  priced_through: string | null;
  market_history_row_count: number;
};

export type SyntheticBondCatalogueResponse = {
  results: SyntheticBondCatalogueEntry[];
};

export type SyntheticBondReferencePoint = {
  timestamp: string;
  bid: number | null;
  ask: number | null;
  bid_yield: number | null;
  ask_yield: number | null;
};

export type SyntheticBondReferenceCurveSlotStatus =
  | 'unselected'
  | 'not_found'
  | 'currency_mismatch'
  | 'no_data'
  | 'available';

export type SyntheticBondReferenceCurveSlot = {
  tenor_months: number;
  tenor_label: string;
  status: SyntheticBondReferenceCurveSlotStatus;
  asset: SyntheticBondSourceAsset | null;
  reason?: string;
  points: SyntheticBondReferencePoint[];
};

export type SyntheticBondReferenceCurvesResponse = {
  currency: SyntheticBondGenerationCurrency;
  tenors: SyntheticBondReferenceCurveSlot[];
  missing_tenor_months?: number[];
};

export type SyntheticBondFinding = {
  code?: string;
  severity?: string;
  message?: string;
  detail?: string;
  [key: string]: unknown;
};

export type SyntheticBondCandidate = {
  id?: string;
  uuid?: string;
  batch_item_id?: string;
  tenor_years?: number;
  initial_tenor_years?: number;
  issue_date?: string;
  legal_issue_date?: string;
  maturity_date?: string;
  legal_maturity_date?: string;
  coupon?: number;
  name?: string;
  ticker?: string;
  ric?: string;
  isin?: string;
  status?: string;
  expected_row_count?: number;
  projected_row_count?: number;
  priced_from?: string | null;
  priced_through?: string | null;
  first_unpriced_date?: string | null;
  fixing_timestamp?: string | null;
  fixing_source_timestamp?: string | null;
  fixing_age_seconds?: number | null;
  warnings?: SyntheticBondFinding[];
  blockers?: SyntheticBondFinding[];
  findings?: SyntheticBondFinding[];
  hard_blockers?: SyntheticBondFinding[];
  fixing?: {
    source_market_history_id?: number;
    source_asset_id?: number;
    timestamp?: string;
    age_days?: number;
    bid_yield?: number;
    ask_yield?: number;
  };
  identifiers?: {
    name?: string;
    ticker?: string;
    ric?: string;
    isin?: string;
  };
  existing_artifact_id?: string | null;
  result?: Record<string, unknown>;
  error?: Record<string, unknown>;
  [key: string]: unknown;
};

export type SyntheticBondBatchPreview = {
  id?: string;
  uuid?: string;
  batch_id?: string;
  preview_id?: string;
  preview_checksum?: string;
  checksum?: string;
  status?: string;
  generation_as_of_date?: string;
  family_revision_id?: string;
  family_definition_hash?: string;
  family_hash?: string;
  issue_dates?: string[];
  asset_candidate_count?: number;
  candidate_count?: number;
  expected_market_history_rows?: number;
  projected_market_history_rows?: number;
  existing_count?: number;
  blocked_count?: number;
  warnings?: SyntheticBondFinding[];
  blockers?: SyntheticBondFinding[];
  findings?: SyntheticBondFinding[];
  items?: SyntheticBondCandidate[];
  candidates?: SyntheticBondCandidate[];
  tenor_summaries?: Record<string, unknown>[];
  source_assets?: SyntheticBondSourceAsset[];
  family_metadata?: SyntheticBondFamilyMetadata;
  canonical_request?: SyntheticBondGenerationDraft;
  family?: {
    id: string;
    definition_hash: string;
    currency: SyntheticBondGenerationCurrency;
    source_nodes: Array<{
      tenor_months?: number;
      tenor_years?: number;
      tenor_label?: string;
      source_asset_id: number;
      model: string;
      name: string;
      ticker?: string | null;
      ric?: string | null;
    }>;
    family_metadata: SyntheticBondFamilyMetadata;
    market_hours?: Record<string, unknown>;
  };
  versions?: {
    publication?: string;
    family_definition_schema?: string;
    pricing?: string;
    identifier_scheme?: string;
    identifier_key_id?: string;
  };
  summary?: {
    currency?: SyntheticBondGenerationCurrency;
    initial_tenors?: number[];
    issue_dates?: string[];
    candidate_total?: number;
    projected_market_history_total?: number;
    blocked_total?: number;
    existing_total?: number;
  };
  progress?: {
    candidate_total?: number;
    completed_total?: number;
    existing_total?: number;
    failed_total?: number;
    market_history_total?: number;
  };
  error?: Record<string, unknown>;
  [key: string]: unknown;
};

export type SyntheticBondCandidatePricePreview = {
  batch_item_id?: string;
  item?: SyntheticBondCandidate;
  issued_bond?: {
    price_series?: IssuedBondPricePoint[];
    [key: string]: unknown;
  };
  price_series?: IssuedBondPricePoint[];
  points?: IssuedBondPricePoint[];
  [key: string]: unknown;
};

export type SyntheticBondPreviewJobV2 = {
  preview_job_id: string;
  status: 'PENDING' | 'RUNNING' | 'SUCCEEDED' | 'FAILED';
  status_url: string;
  batch_id?: string | null;
  client_request_id?: string;
  client_draft_revision?: number | null;
  candidate_total?: number;
  error?: Record<string, unknown>;
  created_at?: string;
  started_at?: string | null;
  finished_at?: string | null;
  preview?: SyntheticBondBatchPreview;
  candidate_previews?: Record<string, SyntheticBondCandidatePricePreview>;
};

export type SyntheticBondPreviewCandidateV2Event = {
  preview_job_id: string;
  batch_id: string;
  item_id: string;
  client_request_id?: string;
  client_draft_revision?: number | null;
  chunk_index?: number;
  chunk_total?: number;
  price_point_total?: number;
  candidate_preview: SyntheticBondCandidatePricePreview;
};

export type SyntheticBondPreviewCandidateChunkBufferV2 = {
  candidate_preview: SyntheticBondCandidatePricePreview;
  chunks: Record<string, IssuedBondPricePoint[]>;
  chunk_total: number;
  price_point_total?: number;
};

export type SyntheticBondBatchStatus = {
  id?: string;
  uuid?: string;
  batch_id?: string;
  status?: string;
  total_count?: number;
  pending_count?: number;
  completed_count?: number;
  existing_count?: number;
  failed_count?: number;
  current_candidate?: string;
  items?: SyntheticBondCandidate[];
  results?: SyntheticBondCandidate[];
  errors?: SyntheticBondFinding[];
  status_url?: string;
  preview_checksum?: string;
  generation_as_of_date?: string;
  family?: SyntheticBondBatchPreview['family'];
  versions?: SyntheticBondBatchPreview['versions'];
  summary?: SyntheticBondBatchPreview['summary'];
  progress?: SyntheticBondBatchPreview['progress'];
  error?: Record<string, unknown>;
  [key: string]: unknown;
};

export type SyntheticBondCacheEntry<T> = {
  data?: T;
  fetched: boolean;
  fetching: boolean;
  error?: string;
};

export type SyntheticBondGenerationState = {
  options?: SyntheticBondGenerationOptions;
  optionsFetching: boolean;
  optionsError?: string;
  series?: SyntheticBondSeries[];
  seriesFetching: boolean;
  seriesError?: string;
  seriesSaving: boolean;
  seriesSaveError?: string;
  catalogue?: SyntheticBondCatalogueEntry[];
  catalogueFetching: boolean;
  catalogueError?: string;
  sourceAssetSearchByKey: Record<
    string,
    SyntheticBondCacheEntry<SyntheticBondSourceAssetSearchResponse>
  >;
  referenceCurvePresetsByCurrencyKey: Record<
    string,
    SyntheticBondCacheEntry<SyntheticBondReferenceCurvePresetResponse>
  >;
  selectedReferenceCurvePresetId?: string;
  referenceCurvePresetSaving: boolean;
  referenceCurvePresetSaveError?: string;
  referenceCurvesByFamilyKey: Record<
    string,
    SyntheticBondCacheEntry<SyntheticBondReferenceCurvesResponse>
  >;
  candidatePreviewByKey: Record<
    string,
    SyntheticBondCacheEntry<SyntheticBondCandidatePricePreview>
  >;
  draftSpec: SyntheticBondGenerationDraft;
  draftRevision: number;
  preview?: SyntheticBondBatchPreview;
  previewing: boolean;
  previewError?: string;
  previewRequestId?: string;
  previewRequestRevision?: number;
  previewV2JobsById: Record<string, SyntheticBondPreviewJobV2>;
  candidatePreviewsV2ByJobId: Record<
    string,
    Record<string, SyntheticBondCandidatePricePreview>
  >;
  candidatePreviewChunksV2ByJobId: Record<
    string,
    Record<string, SyntheticBondPreviewCandidateChunkBufferV2>
  >;
  activePreviewV2JobId?: string;
  previewV2?: SyntheticBondBatchPreview;
  previewingV2: boolean;
  previewV2Error?: string;
  previewV2RequestId?: string;
  previewV2RequestRevision?: number;
  previewV2StatusFetching: boolean;
  createSubmitting: boolean;
  createError?: string;
  activeBatchId?: string;
  activeBatch?: SyntheticBondBatchStatus;
  statusFetching: boolean;
  statusError?: string;
  statusRequestId?: string;
  retrying: boolean;
  retryError?: string;
  retryRequestId?: string;
};

export const syntheticBondSourceSearchKey = (currencyId: number) =>
  String(currencyId);

export const syntheticBondReferenceCurvePresetKey = (currencyId: number) =>
  String(currencyId);

export const syntheticBondSourceMapsEqual = (
  left: SyntheticBondSourceMap,
  right: SyntheticBondSourceMap,
) =>
  SYNTHETIC_BOND_CURVE_TENOR_MONTHS.every(
    tenor => left[String(tenor)] === right[String(tenor)],
  );

export const syntheticBondFamilyKey = (
  currencyId: number | null,
  sources: SyntheticBondSourceMap,
) =>
  [
    currencyId ?? 'none',
    ...SYNTHETIC_BOND_CURVE_TENOR_MONTHS.map(
      tenor => sources[String(tenor)] ?? 'none',
    ),
  ].join(':');

export const syntheticBondDraftKey = (draft: SyntheticBondGenerationDraft) =>
  JSON.stringify({
    series_id: draft.series_id,
    currency_id: draft.currency_id,
    family_label: draft.family_label,
    family_metadata: draft.family_metadata,
    initial_tenors: [...draft.initial_tenors].sort((a, b) => a - b),
    first_issue_date: draft.first_issue_date,
    last_issue_date: draft.last_issue_date,
    target_kind: draft.target_kind,
    source_asset_ids_by_tenor_months: SYNTHETIC_BOND_CURVE_TENOR_MONTHS.reduce(
      (result, tenor) => ({
        ...result,
        [tenor]: draft.source_asset_ids_by_tenor_months[String(tenor)],
      }),
      {} as SyntheticBondSourceMap,
    ),
  });

export const syntheticBondPreviewId = (preview?: SyntheticBondBatchPreview) =>
  preview?.batch_id || preview?.id || preview?.uuid || preview?.preview_id;

export const syntheticBondPreviewChecksum = (
  preview?: SyntheticBondBatchPreview,
) => preview?.preview_checksum || preview?.checksum;

export const syntheticBondCandidateId = (candidate?: SyntheticBondCandidate) =>
  candidate?.batch_item_id || candidate?.id || candidate?.uuid;

export const isSyntheticBondBatchTerminal = (status?: string) =>
  [
    'SUCCEEDED',
    'SUCCESS',
    'COMPLETED',
    'PARTIAL_FAILURE',
    'PARTIAL_FAILED',
    'FAILED',
    'ENQUEUE_FAILED',
    'STALE',
  ].includes((status || '').toUpperCase());
